import { TikTokApiResponse, TikTokAuthor } from "@/types/api";
import { BASE_URL, API_TIMEOUT } from "./api/config";
import { fetchWithTimeout } from "./api/fetchUtils";

async function resolveShortUrl(url: string): Promise<string> {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      redirect: 'follow'
    });
    return response.url;
  } catch (error) {
    console.error('Failed to resolve short URL:', error);
    return url; // Return original URL if resolution fails
  }
}

export async function fetchTikTokVideos(url: string, cursor: string = "0"): Promise<TikTokApiResponse> {
  if (!url.trim()) {
    throw new Error('Please enter a TikTok URL');
  }

  try {
    // Resolve URL if it's a mobile short URL
    const isMobileUrl = url.includes('/t/');
    const resolvedUrl = isMobileUrl ? await resolveShortUrl(url) : url;
    console.log('Resolved URL:', resolvedUrl);

    const params = new URLSearchParams({
      url: resolvedUrl,
      cursor: cursor
    });

    const apiUrl = `${BASE_URL}?${params.toString()}`;
    console.log('Making API request to:', apiUrl);

    const response = await fetchWithTimeout(apiUrl, 'GET', API_TIMEOUT);

    if (!response) {
      throw new Error('No response received from API');
    }

    if (response.error) {
      throw new Error(response.error || 'Unknown API error');
    }

    // Handle various response formats more gracefully
    return transformApiResponse(response.data || response);
  } catch (error) {
    console.error('API call failed:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch videos');
  }
}

function transformApiResponse(data: any): TikTokApiResponse {
  // More resilient data extraction
  const itemsArray = Array.isArray(data.itemList) ? data.itemList
    : Array.isArray(data.videos) ? data.videos
    : Array.isArray(data.items) ? data.items
    : Array.isArray(data) ? data
    : [];

  if (itemsArray.length === 0 && !data.cursor) {
    throw new Error(
      "No videos found in response. This might be due to API rate limiting or an empty collection."
    );
  }

  // Extract cursor with fallbacks
  const nextCursor = String(
    data.cursor ?? 
    data.hasMore?.cursor ?? 
    data.nextCursor ?? 
    data.next_cursor ?? 
    "0"
  );

  // Transform videos with better error handling
  const videos = itemsArray.map((item: any) => {
    const videoData = item.video || item;
    const description = item.desc || videoData.desc || item.description || "";
    
    let author: string | TikTokAuthor;
    if (typeof item.author === 'string') {
      author = item.author;
    } else if (item.author) {
      author = item.author;
    } else {
      author = item.creator_name || item.uniqueId || "Unknown creator";
    }
    
    const video_url = videoData.playAddr || videoData.video_url || videoData.downloadAddr || "";
    const thumbnail = videoData.cover || videoData.thumbnail || videoData.originCover || videoData.dynamicCover || "";
    const duration = Number(videoData.duration) || 0;

    return {
      id: item.id || videoData.id || String(Math.random()),
      description,
      author,
      duration,
      thumbnail,
      video_url,
      status: video_url ? "available" : "unavailable"
    };
  });

  return {
    videos,
    next_cursor: nextCursor,
    hasMore: data.hasMore === true || nextCursor !== "0",
  };
}
